import React from 'react';
import { Typography, Row, Col, Card } from 'antd';
import { Link } from 'react-router-dom';
import {
  Rocket,
  Users,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Layers,
  Workflow,
  Globe,
  Cpu,
  Sparkles,
  BookOpen,
} from 'lucide-react';
import useSEO from '../../hooks/useSEO';
import PublicHeader from '../../components/Public/PublicHeader';
import PublicFooter from '../../components/Public/PublicFooter';
import { getAppDomainUrl } from '../../utils/constants';
import { W7C_ECOSYSTEM_PRESENTATIONS_URL } from '../../utils/externalLinks';
/* STYLESYSTEM/pages/public-marketing.css */

const { Title, Paragraph, Text } = Typography;

const AboutPage = () => {
  useSEO('about');

  /** Ключевые опоры (Часть V эталонной документации). */
  const platformBlocks = [
    {
      icon: <Zap size={20} style={{ color: 'var(--warn)' }} />,
      title: 'Единый товарный контур',
      description:
        'Товары, заказы, склады, доставка, документы, CRM, финансы и аналитика работают в связанном контуре — без независимых «теневых каталогов».',
    },
    {
      icon: <Shield size={20} style={{ color: 'var(--ok)' }} />,
      title: 'Безопасность и изоляция',
      description:
        'Права, компании, документы и файлы разделяются по server-derived контексту. Публичный UI не раскрывает приватные данные.',
    },
    {
      icon: <Users size={20} style={{ color: 'var(--brand)' }} />,
      title: 'B2B и B2C сценарии',
      description:
        'Своя витрина, Telegram-сценарии, B2B-кабинеты, услуги, склады, интеграции и потребительский маркетплейс Delgram — на одной модели данных.',
    },
    {
      icon: <Rocket size={20} style={{ color: 'var(--brand)' }} />,
      title: 'ВЭЙГПТ и ассистенты',
      description:
        '20+ AI-ассистентов помогают с товарами, контентом, аналитикой, юридическими задачами и поддержкой клиентов прямо внутри платформы.',
    },
  ];

  /** Что изучить в первую очередь — компактный чек-лист новичку. */
  const studyPoints = [
    'Как устроены модули торговли: Shop, Orders, Logistics, Delivery, Documents, CRM и Finance.',
    'Как подключаются витрины, личные кабинеты, Telegram и внешние интеграции — без потери данных.',
    'Где посмотреть тарифы, юридические документы, сведения об организации и инструкции.',
    'Как работает безопасность: вход, роли, company-context, согласия и изоляция данных компаний.',
    'Чем отличаются торговая логистика и WMS, как из заказа рождаются отгрузка, доставка и документы.',
    'Как поставщики, ваш каталог и маркетплейсы сводятся в единые справочники без дублей.',
  ];

  /** «Боли», которые закрывает экосистема (Часть I эталона). */
  const painPoints = [
    {
      title: 'Разрозненные системы',
      text: 'Склад в одной программе, сайт в другой, маркетплейсы — в личных кабинетах. Заказы переносятся вручную, остатки расходятся, цены и описания живут параллельной жизнью.',
    },
    {
      title: 'Дубли и «разъехавшиеся» карточки',
      text: 'Один и тот же товар от разных поставщиков превращается в несколько записей. Markbase сводит их через штрихкод, артикул, бренд и категории — с контролем человека.',
    },
    {
      title: 'Непрозрачная логистика',
      text: 'На сайте одни сроки, у поставщика — свои условия. Markbase обещает покупателю то, что реально исполнимо в регламенте склада и доставки.',
    },
    {
      title: 'Односторонние интеграции',
      text: 'Выгрузить каталог — половина дела. Markbase забирает обратно карточки и заказы маркетплейсов в единую модель с этапом проверки.',
    },
    {
      title: 'Размытый доступ',
      text: 'Кто меняет цены, кто видит чужой склад, кто утверждает закупку — единая модель компаний, ролей и видимости заменяет хаос и споры.',
    },
    {
      title: 'ИИ как отдельная игрушка',
      text: 'ВЭЙГПТ работает в контексте компании и тарифа: не абстрактный чат, а ассистенты, которые видят ваши данные с правильными правами.',
    },
  ];

  /** Сквозной поток данных «от прайса до денег» (Часть II эталона). */
  const flowSteps = [
    { title: 'Импорт', text: 'Файлы или API — приём данных от поставщиков и маркетплейсов.' },
    { title: 'Нормализация', text: 'Названия, единицы, категории и атрибуты — к вашей схеме.' },
    { title: 'Сопоставление', text: 'Связь поставщика и вашего каталога без дублей.' },
    { title: 'Каталог', text: 'Единая мастер-карточка товара — источник истины компании.' },
    { title: 'Витрина Shop', text: 'Публикация, кеширование, SEO, ускорение страниц.' },
    { title: 'Заказ', text: 'Состав, цены, статусы, клиент — без копирования каталога.' },
    { title: 'Склад / WMS', text: 'Резерв, отгрузка, ячейки, инвентаризация.' },
    { title: 'Доставка', text: 'Варианты, стоимость, зоны, связь со службами.' },
    { title: 'Деньги', text: 'Эквайринг, кошелёк, фискальный чек, кассовый контур.' },
    { title: 'Документы', text: 'ЭДО, акты, счета, маркировка — закрытие цикла.' },
  ];

  /** Опорные модули (Часть XVIII эталона), кратко на странице «О платформе». */
  const moduleClusters = [
    {
      icon: <Layers size={18} />,
      title: 'Торговля и клиентский опыт',
      items: [
        'Shop — витрина, корзина, оформление заказа',
        'Orders — жизненный цикл заказа',
        'Product Import — нормализация прайсов',
        'CRM — клиенты, сделки, коммуникации',
        'Loyalty / Bonus — программы лояльности и QR',
      ],
    },
    {
      icon: <Workflow size={18} />,
      title: 'Логистика, склад, доставка',
      items: [
        'Logistics — склады и отгрузки',
        'SKLAD — WMS, зоны, ячейки, инвентаризация',
        'Delivery — варианты, стоимость, статусы',
        'Documents — счета, акты, ЭДО, маркировка',
        'Kassa — кассовые смены и фискальные чеки',
      ],
    },
    {
      icon: <Globe size={18} />,
      title: 'Каналы продаж и кейсы',
      items: [
        'Integrations — маркетплейсы, банки, ЭДО',
        'MPLAZA / Delgram — потребительский маркетплейс',
        'Market — B2B-закупки и сравнение поставщиков',
        'Services — маркетплейс услуг специалистов',
        'vde76 — пример полноценного B2B/B2C-портала',
      ],
    },
    {
      icon: <Cpu size={18} />,
      title: 'AI и интеллект',
      items: [
        'ВЭЙГПТ — основной AI-контур с 20+ ассистентами',
        'Waygen — генерация маркетингового контента',
        'Assistant — универсальный помощник в витрине и CRM',
        'Search — поиск по платформе',
        'SEO — фиды и индексация витрины',
      ],
    },
    {
      icon: <Shield size={18} />,
      title: 'Ядро и безопасность',
      items: [
        'UAM — единый вход и связи пользователей с компаниями',
        'Security — лимиты, списки доступа, аудит',
        'Billing / Wallet — тарифы, лимиты, баланс',
        'Scheduler — фоновые и периодические задачи',
        'Monitoring — метрики и живость сервисов',
      ],
    },
    {
      icon: <Sparkles size={18} />,
      title: 'Аналитика и поддержка',
      items: [
        'Analytics — KPI, отчёты, прогнозы',
        'Process Center — исполнение бизнес-процессов',
        'Support — обращения пользователей',
        'POWER — производственная эффективность',
        'Compliance / LEGAL — соответствие и юридический контур',
      ],
    },
  ];

  /** Принципы (Часть III и часть «Заключение»). */
  const principles = [
    'Один товар, один заказ, один источник истины — без «второй базы» рядом.',
    'Поэтапное внедрение фазами: каталог → склад → маркетплейсы → документы.',
    'Изоляция компаний и ролей — данные и файлы не путаются между организациями.',
    'События между модулями надёжно переживают сбои и повторы.',
    'ИИ работает в контексте тарифа и не имеет произвольного доступа к коду продукта.',
  ];

  return (
    <div className="public-page">
      <PublicHeader />

      {/* Hero */}
      <section className="page-hero">
        <div className="container">
          <Title level={1}>О платформе Markbase</Title>
          <Paragraph className="hero-subtitle">
            Markbase объединяет торговлю, склад, доставку, документы, аналитику, CRM и AI-ассистентов в одной
            модульной платформе. Это не набор отдельных программ, а единая среда с общими правилами доступа,
            тарифом компании и согласованными данными.
          </Paragraph>
        </div>
      </section>

      {/* Миссия */}
      <section className="content-section">
        <div className="container">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <Title level={2}>Наша миссия</Title>
              <Paragraph className="large-text">
                Дать бизнесу единый рабочий контур вместо набора разрозненных сервисов: один аккаунт, одна
                компания, единые права, модули и данные. Платформа подходит и для стартовой компании, и для
                зрелого B2B/B2C-контура — растёт вместе с задачами без смены основы.
              </Paragraph>
              <Paragraph className="large-text">
                Публичный сайт помогает быстро изучить возможности, тарифы, юридическую информацию,
                документацию и способы связи с оператором. Низ страницы и раздел «Документация» — это
                справочник, который удобно открывать без перечитывания всего повествования.
              </Paragraph>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="highlight-card">
                <Title level={3}>
                  <Rocket size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />
                  Что изучить в первую очередь
                </Title>
                <ul className="benefits-list">
                  {studyPoints.map((item) => (
                    <li key={item}>
                      <CheckCircle size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />
                      {item}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <Link to="/docs" className="footer-link" style={{ fontWeight: 600 }}>
                    Открыть документацию <ArrowRight size={14} style={{ verticalAlign: 'middle' }} />
                  </Link>
                  <Link to="/pricing" className="footer-link" style={{ fontWeight: 600 }}>
                    Перейти к тарифам <ArrowRight size={14} style={{ verticalAlign: 'middle' }} />
                  </Link>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* Ключевые контуры */}
      <section className="content-section gray-bg">
        <div className="container">
          <Title level={2} className="section-title">Ключевые контуры</Title>
          <Row gutter={[32, 32]}>
            {platformBlocks.map((value, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card className="value-card" hoverable>
                  <div className="value-icon">{value.icon}</div>
                  <Title level={4}>{value.title}</Title>
                  <Paragraph>{value.description}</Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Боли, которые закрываем */}
      <section className="content-section">
        <div className="container">
          <Title level={2} className="section-title">Боли, которые закрывает экосистема</Title>
          <Paragraph className="large-text" style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 48px' }}>
            Markbase не «ещё одна программа рядом с десятью существующими». Платформа собирается из модулей,
            которые ссылаются друг на друга по заранее заданным ролям — без параллельных каталогов и
            рассогласованных процессов.
          </Paragraph>
          <Row gutter={[24, 24]}>
            {painPoints.map((item) => (
              <Col xs={24} md={12} lg={8} key={item.title}>
                <Card className="value-card" hoverable>
                  <Title level={4}>{item.title}</Title>
                  <Paragraph>{item.text}</Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Сквозной поток */}
      <section className="content-section gray-bg">
        <div className="container">
          <Title level={2} className="section-title">Сквозной поток: от прайса до денег</Title>
          <Paragraph className="large-text" style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 48px' }}>
            Каждый шаг отвечает за свою зону, но опирается на общую модель компании. Сбои и повторы между
            модулями переживаются без потери смысла заказа.
          </Paragraph>
          <Row gutter={[16, 16]}>
            {flowSteps.map((step, index) => (
              <Col xs={12} sm={8} md={6} lg={index < 5 ? 4 : 4} key={step.title}>
                <Card className="value-card" hoverable>
                  <Text style={{ color: 'var(--brand)', fontWeight: 800 }}>{String(index + 1).padStart(2, '0')}</Text>
                  <Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>{step.title}</Title>
                  <Paragraph style={{ fontSize: 13, margin: 0 }}>{step.text}</Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Модули кратко */}
      <section className="content-section">
        <div className="container">
          <Title level={2} className="section-title">Модули — кратко по группам</Title>
          <Paragraph className="large-text" style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto 48px' }}>
            Перечень собран по группам ценности. Полный справочник модулей и связей доступен в разделе
            «Документация» — там же лежат регламенты внедрения и FAQ для тех, кто отвечает за внедрение.
          </Paragraph>
          <Row gutter={[24, 24]}>
            {moduleClusters.map((cluster) => (
              <Col xs={24} md={12} lg={8} key={cluster.title}>
                <Card className="value-card" hoverable>
                  <div className="value-icon" aria-hidden>{cluster.icon}</div>
                  <Title level={4}>{cluster.title}</Title>
                  <ul className="benefits-list" style={{ marginTop: 8 }}>
                    {cluster.items.map((it) => (
                      <li key={it}>
                        <CheckCircle size={12} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                        {it}
                      </li>
                    ))}
                  </ul>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Принципы */}
      <section className="content-section gray-bg">
        <div className="container">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <Title level={2}>Принципы платформы</Title>
              <Paragraph className="large-text">
                Markbase разрабатывается под боевую эксплуатацию: отказоустойчивость, наблюдаемость, защита
                данных пользователей. Эволюция платформы — инкрементальная, без сценариев «всё или ничего».
              </Paragraph>
              <ul className="benefits-list">
                {principles.map((p) => (
                  <li key={p}>
                    <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    {p}
                  </li>
                ))}
              </ul>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="highlight-card">
                <Title level={3}>Куда идти дальше</Title>
                <Paragraph className="large-text" style={{ marginTop: 12 }}>
                  Если вы изучаете продукт впервые — пройдите по разделам в этом порядке:
                </Paragraph>
                <ol style={{ paddingLeft: 20, color: '#cbd5e1', lineHeight: 1.8 }}>
                  <li><Link to="/" className="footer-link">Главная</Link> — потоки и опоры одной страницей</li>
                  <li><Link to="/pricing" className="footer-link">Тарифы</Link> — лимиты, модули и условия</li>
                  <li><Link to="/docs" className="footer-link">Документация</Link> — гайды и регламенты</li>
                  <li>
                    <a href={W7C_ECOSYSTEM_PRESENTATIONS_URL} className="footer-link" target="_blank" rel="noopener noreferrer">
                      Презентации продуктов экосистемы
                    </a>{' '}
                    — студия w7c: слайды с обзорами и ссылкой на тарифы
                  </li>
                  <li><Link to="/company" className="footer-link">Сведения об организации</Link> — реквизиты</li>
                  <li><Link to="/contact" className="footer-link">Контакты</Link> — связь с командой</li>
                </ol>
                <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <a href={getAppDomainUrl('/register')} className="footer-link" style={{ fontWeight: 700 }}>
                    Начать бесплатно <ArrowRight size={14} style={{ verticalAlign: 'middle' }} />
                  </a>
                  <Link to="/docs" className="footer-link" style={{ fontWeight: 700 }}>
                    <BookOpen size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Открыть документацию
                  </Link>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* Финальный CTA */}
      <section className="content-section">
        <div className="container">
          <Card className="highlight-card">
            <Title level={2}>Где получить полную информацию</Title>
            <Paragraph className="large-text">
              Тарифы — на странице «Тарифы»; юридические документы — в подвале сайта; реквизиты оператора — на
              странице «Сведения об организации»; технические и пользовательские инструкции — в разделе
              «Документация». Если после чтения остались вопросы, это нормально: документ задаёт картину
              возможностей и границ, а индивидуальные сценарии закрываются в диалоге с продажами и поддержкой.
            </Paragraph>
          </Card>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default AboutPage;
